import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiShoppingCart,
  FiCheck,
  FiDownload,
  FiX,
  FiStar,
  FiEye,
  FiPlay,
} from "react-icons/fi";
import { BsLightningCharge } from "react-icons/bs";
import { GlassCard } from "../components/ui/GlassCard";
import { LoadingSkeleton } from "../components/ui/LoadingSkeleton";
import * as celebUpdateService from "../services/celebUpdateService";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../store/slices/authSlice";

export const CelebUpdates = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [celebUpdates, setCelebUpdates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchasedUpdates, setPurchasedUpdates] = useState([]);
  const [showMyPurchases, setShowMyPurchases] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);

  useEffect(() => {
    fetchCelebUpdates();
    if (user?.id) {
      dispatch(getProfile());
      fetchMyPurchases();
    }
  }, [dispatch, user?.id]);

  const fetchCelebUpdates = async () => {
    try {
      setIsLoading(true);
      const response = await celebUpdateService.getCelebUpdates();
      setCelebUpdates(response.celebUpdates || []);
    } catch (error) {
      toast.error("Failed to load tools & updates");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMyPurchases = async () => {
    try {
      const response = await celebUpdateService.getMyPurchasedCelebUpdates();
      setPurchasedUpdates(response.purchases || []);
    } catch (error) {
      console.error("Failed to load purchases");
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error("Please login to purchase");
      return;
    }

    try {
      setIsPurchasing(true);
      await celebUpdateService.purchaseCelebUpdate(selectedUpdate._id);
      toast.success(
        'Purchase successful! Check "My Purchases" to view the link',
      );
      setShowPurchaseModal(false);
      setSelectedUpdate(null);
      dispatch(getProfile());
      fetchMyPurchases();
    } catch (error) {
      toast.error(error.response?.data?.message || "Purchase failed");
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleViewPurchase = async (purchase) => {
    try {
      const response = await celebUpdateService.getPurchasedCelebUpdateDetails(
        purchase._id,
      );
      setSelectedPurchase(response.purchase);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error("Failed to load purchase details");
    }
  };

  const isPurchased = (updateId) => {
    return purchasedUpdates.some((p) => p?.celebUpdate?._id === updateId);
  };

  const getFilteredUpdates = () => {
    let filtered = [...celebUpdates];

    // Search
    if (searchTerm) {
      filtered = filtered.filter(
        (u) =>
          u.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.description.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Sort
    if (sortBy === "price_low") {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === "price_high") {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortBy === "popular") {
      filtered.sort((a, b) => b.purchaseCount - a.purchaseCount);
    }

    return filtered;
  };

  const filteredUpdates = getFilteredUpdates();

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: { transition: { staggerChildren: 0.1 } },
  };

  const pageTitle = "Social Media Tools & Featured Updates | Nitro Logs";
  const pageDescription =
    "Explore exclusive social media tools, celebrity updates, and verified growth resources that help brands scale and maintain account security.";
  const siteUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://nitrologs.com";
  const pageUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `${siteUrl}/tools-updates`;
  const imageUrl = `${siteUrl}/nitrologs.png`;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: pageUrl,
    name: pageTitle,
    description: pageDescription,
    publisher: {
      "@type": "Organization",
      name: "Nitro Logs",
      url: siteUrl,
      logo: `${siteUrl}/nitrologs.png`,
    },
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="social media tools, celebrity updates, account growth tools, verified updates, social media security, premium downloads"
        />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta
          property="og:image:alt"
          content="Nitro Logs social media tools and updates"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-luxury-50 via-white to-luxury-100 py-6 sm:py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-luxury-900">
                Tools & <span className="gradient-text">Updates</span>
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-luxury-600 mt-2">
                Exclusive tools and updates
              </p>
            </div>

            {user && purchasedUpdates.length > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowMyPurchases(!showMyPurchases)}
                className="glass px-4 sm:px-6 py-2 sm:py-3 rounded-xl flex items-center space-x-2 text-primary-600 hover:bg-primary-50 transition-colors"
              >
                <FiDownload className="w-4 sm:w-5 h-4 sm:h-5" />
                <span className="text-xs sm:text-sm font-medium">
                  My Purchases ({purchasedUpdates.length})
                </span>
              </motion.button>
            )}
          </motion.div>

          {/* View Toggle */}
          {showMyPurchases ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="mb-6 sm:mb-8">
                <button
                  onClick={() => setShowMyPurchases(false)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium mb-4"
                >
                  ← Back to all updates
                </button>
                <h2 className="text-xl sm:text-2xl font-bold text-luxury-900">
                  My Purchased Updates
                </h2>
              </div>

              {purchasedUpdates.length > 0 ? (
                <motion.div
                  variants={staggerContainer}
                  initial="initial"
                  animate="animate"
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                >
                  {purchasedUpdates.map((purchase) => (
                    <motion.div
                      key={purchase._id}
                      variants={fadeInUp}
                      whileHover={{ y: -5 }}
                      onClick={() => handleViewPurchase(purchase)}
                      className="glass p-4 sm:p-6 rounded-2xl cursor-pointer border-2 border-green-500 hover:shadow-luxury-lg transition-all duration-300"
                    >
                      <div className="flex items-start justify-between mb-3 sm:mb-4">
                        <div className="flex-1">
                          <h3 className="text-sm sm:text-base font-bold text-luxury-900 line-clamp-2">
                            {purchase.celebUpdate.title}
                          </h3>
                        </div>
                        <FiCheck className="w-5 h-5 text-green-500 flex-shrink-0 ml-2" />
                      </div>
                      <p className="text-xs sm:text-sm text-luxury-600 line-clamp-2 mb-4">
                        {purchase.celebUpdate.description}
                      </p>
                      <button className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-2 text-xs sm:text-sm font-medium rounded-xl hover:shadow-lg transition-all flex items-center justify-center space-x-2">
                        <FiPlay className="w-4 h-4" />
                        <span>View Details</span>
                      </button>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <GlassCard className="p-8 text-center">
                  <div className="w-16 h-16 bg-luxury-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiShoppingCart className="w-8 h-8 text-luxury-400" />
                  </div>
                  <p className="text-luxury-600 mb-4">No purchases yet</p>
                  <button
                    onClick={() => setShowMyPurchases(false)}
                    className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                  >
                    Browse updates
                  </button>
                </GlassCard>
              )}
            </motion.div>
          ) : (
            <>
              {/* Filters Bar */}
              <motion.div
                variants={fadeInUp}
                initial="initial"
                animate="animate"
                className="glass rounded-2xl p-4 mb-8"
              >
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1 relative">
                    <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400 w-4 sm:w-5" />
                    <input
                      type="text"
                      placeholder="Search updates..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input-luxury pl-12 w-full text-xs sm:text-sm"
                    />
                  </div>

                  {/* Filter Toggle */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl flex items-center space-x-2 transition-all text-xs sm:text-sm flex-shrink-0 ${
                      showFilters
                        ? "bg-primary-600 text-white"
                        : "glass hover:bg-primary-50 text-luxury-700"
                    }`}
                  >
                    <FiFilter className="w-4 sm:w-5 h-4 sm:h-5" />
                    <span>Filters</span>
                    <FiChevronDown
                      className={`w-4 h-4 transition-transform duration-300 ${
                        showFilters ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {/* Filter Panel */}
                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-6 mt-4 border-t border-luxury-200">
                        <div className="grid grid-cols-1 gap-4">
                          {/* Sort */}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-luxury-700 mb-2">
                              Sort By
                            </label>
                            <select
                              value={sortBy}
                              onChange={(e) => setSortBy(e.target.value)}
                              className="input-luxury w-full text-xs sm:text-sm"
                            >
                              <option value="newest">Newest First</option>
                              <option value="price_low">
                                Price: Low to High
                              </option>
                              <option value="price_high">
                                Price: High to Low
                              </option>
                              <option value="popular">Most Purchased</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Celeb Updates Grid */}
              {isLoading ? (
                <LoadingSkeleton count={6} />
              ) : filteredUpdates.length > 0 ? (
                <motion.div
                  variants={staggerContainer}
                  initial="initial"
                  animate="animate"
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                >
                  {filteredUpdates.map((update) => {
                    const purchased = isPurchased(update._id);
                    return (
                      <motion.div
                        key={update._id}
                        variants={fadeInUp}
                        whileHover={{ y: -5 }}
                        className="glass p-4 sm:p-6 rounded-2xl border border-luxury-200 hover:shadow-luxury-lg transition-all duration-300 flex flex-col"
                      >
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3 sm:mb-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm sm:text-base font-bold text-luxury-900 line-clamp-2">
                              {update.title}
                            </h3>
                            {update.purchaseCount > 0 && (
                              <div className="flex items-center space-x-2 mt-2">
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full flex items-center space-x-1 text-xs">
                                  <BsLightningCharge className="w-3 h-3" />
                                  <span>{update.purchaseCount} sold</span>
                                </span>
                              </div>
                            )}
                          </div>
                          {purchased && (
                            <FiCheck className="w-5 h-5 text-green-500 flex-shrink-0 ml-2" />
                          )}
                        </div>

                        {/* Description */}
                        <p className="text-xs sm:text-sm text-luxury-600 line-clamp-3 mb-4 flex-1">
                          {update.description}
                        </p>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-luxury-200">
                          <div>
                            <p className="text-lg sm:text-xl font-bold gradient-text">
                              ₦{update.price.toLocaleString()}
                            </p>
                            <p className="text-xs text-luxury-500">
                              by {update.addedBy?.username || "Admin"}
                            </p>
                          </div>
                          {purchased ? (
                            <button
                              onClick={() => {
                                const purchase = purchasedUpdates.find(
                                  (p) => p.celebUpdate._id === update._id,
                                );
                                if (purchase) handleViewPurchase(purchase);
                              }}
                              className="px-3 sm:px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:shadow-lg transition-all flex items-center space-x-1"
                            >
                              <FiEye className="w-4 h-4" />
                              <span>View</span>
                            </button>
                          ) : (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setSelectedUpdate(update);
                                setShowPurchaseModal(true);
                              }}
                              className="px-3 sm:px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:shadow-lg transition-all flex items-center space-x-1"
                            >
                              <FiShoppingCart className="w-4 h-4" />
                              <span>Buy</span>
                            </motion.button>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              ) : (
                <GlassCard className="p-8 sm:p-12 text-center">
                  <div className="w-16 h-16 bg-luxury-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiSearch className="w-8 h-8 text-luxury-400" />
                  </div>
                  <p className="text-luxury-600 text-sm">
                    No updates found matching your criteria
                  </p>
                </GlassCard>
              )}
            </>
          )}
        </div>
      </div>

      {/* Purchase Modal */}
      <AnimatePresence>
        {showPurchaseModal && selectedUpdate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPurchaseModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-luxury-900">
                  Confirm Purchase
                </h2>
                <button
                  onClick={() => setShowPurchaseModal(false)}
                  className="text-luxury-400 hover:text-luxury-600"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <GlassCard className="p-4">
                  <h3 className="font-semibold text-luxury-900 mb-2">
                    {selectedUpdate.title}
                  </h3>
                  <p className="text-sm text-luxury-600 mb-3">
                    {selectedUpdate.description}
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t border-luxury-200">
                    <span className="text-luxury-600">Price</span>
                    <span className="text-xl font-bold gradient-text">
                      ₦{selectedUpdate.price.toLocaleString()}
                    </span>
                  </div>
                </GlassCard>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs sm:text-sm text-blue-800">
                    ✓ After purchase, you'll receive access to the exclusive
                    link with all update details
                  </p>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handlePurchase}
                disabled={isPurchasing}
                className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <FiShoppingCart className="w-5 h-5" />
                <span>
                  {isPurchasing ? "Processing..." : "Confirm Purchase"}
                </span>
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Details Modal for Purchased Updates */}
      <AnimatePresence>
        {showDetailsModal && selectedPurchase && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-luxury-900">
                  Update Details
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-luxury-400 hover:text-luxury-600"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <GlassCard className="p-4 bg-gradient-to-r from-green-50 to-green-50 border border-green-200">
                  <h3 className="font-semibold text-luxury-900 mb-2">
                    {selectedPurchase.celebUpdate.title}
                  </h3>
                  <p className="text-sm text-luxury-600 mb-4">
                    {selectedPurchase.celebUpdate.description}
                  </p>
                  <div className="space-y-3 pt-4 border-t border-green-200">
                    <div>
                      <p className="text-xs text-luxury-500 mb-1">
                        Purchase Date
                      </p>
                      <p className="text-sm font-medium text-luxury-900">
                        {new Date(
                          selectedPurchase.completedAt,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-luxury-500 mb-1">
                        Amount Paid
                      </p>
                      <p className="text-lg font-bold gradient-text">
                        ₦{selectedPurchase.amount.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </GlassCard>

                <GlassCard className="p-4">
                  <p className="text-xs text-luxury-500 mb-2 font-semibold uppercase">
                    Exclusive Link
                  </p>
                  <a
                    href={selectedPurchase.accessibleLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium break-all"
                  >
                    {selectedPurchase.accessibleLink}
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(
                        selectedPurchase.accessibleLink,
                      );
                      toast.success("Link copied to clipboard");
                    }}
                    className="mt-3 w-full px-4 py-2 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-200 transition-colors"
                  >
                    Copy Link
                  </button>
                </GlassCard>

                <a
                  href={selectedPurchase.accessibleLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <button className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-2">
                    <FiPlay className="w-5 h-5" />
                    <span>Open Update</span>
                  </button>
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CelebUpdates;
